import { useState, useEffect, useCallback, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { Modal, Tabs, App } from 'antd'
import { Database, FileText, BarChart3, ChevronDown, ChevronRight } from 'lucide-react'
import { useMindMapContext } from '../../../context/mindmap/useMindMapContext'
import { FONT_WEIGHT, TYPOGRAPHY } from '../../../styles/theme'
import {
  RagInspectorService,
  type RagInspectorData,
  type RagInspectorDocument,
} from '../../../services/ragInspector/ragInspectorService'
import {
  InspectorBody,
  TabContent,
  StatsGrid,
  StatCard,
  StatValue,
  StatLabel,
  StatusBadge,
  VentureTable,
  VentureTableHeader,
  VentureTableRow,
  SplitPane,
  DocListPane,
  DocGroupHeader,
  DocRow,
  DocFileName,
  DocMeta,
  DocSummary,
  ChunkPane,
  ChunkCard,
  ChunkHeader,
  ChunkContent,
  TokenBadge,
  LoadMoreButton,
  SourceNodeRow,
  SourceNodeHeader,
  SourceExcerpts,
  ExcerptBlock,
  ExcerptSource,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  LoadingPulse,
  CategoryBadge,
  SectionTitle,
} from './styles/RagInspectorModalStyles'

interface RagInspectorModalProps {
  isVisible: boolean
  onClose: () => void
}

const CHUNKS_PER_PAGE = 20

export function RagInspectorModal({ isVisible, onClose }: RagInspectorModalProps) {
  const { message } = App.useApp()
  const { nodes, mindmapId } = useMindMapContext()

  const [data, setData] = useState<RagInspectorData | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [chunkPage, setChunkPage] = useState(1)
  const [expandedSourceNodes, setExpandedSourceNodes] = useState<Set<string>>(new Set())

  // Build node label lookup
  const nodeLabelMap = useMemo(() => {
    const map = new Map<string, { label: string; category: string }>()
    for (const n of nodes) {
      const data = n.data as Record<string, unknown>
      const label =
        (typeof data.label === 'string' && data.label) ||
        (typeof data.companyName === 'string' && data.companyName) ||
        (typeof data.name === 'string' && data.name) ||
        n.id
      const category = typeof data.category === 'string' ? data.category : 'unknown'
      map.set(n.id, { label, category })
    }
    return map
  }, [nodes])

  // Fetch data when modal opens
  const fetchData = useCallback(async () => {
    if (!mindmapId) return
    setLoading(true)
    try {
      const result = await RagInspectorService.getAll(mindmapId)
      if (result.error) {
        message.error(result.error.message || 'Failed to load RAG data')
        return
      }
      setData(result.data)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap', action: 'load_rag_data' },
      })
      message.error('Failed to load RAG data')
    } finally {
      setLoading(false)
    }
  }, [mindmapId, message])

  useEffect(() => {
    if (isVisible && mindmapId) {
      void fetchData()
      setSelectedDocId(null)
      setChunkPage(1)
    }
  }, [isVisible, mindmapId, fetchData])

  // Group documents by venture node
  const docsByVenture = useMemo(() => {
    if (!data) return new Map<string, RagInspectorDocument[]>()
    const grouped = new Map<string, RagInspectorDocument[]>()
    for (const doc of data.documents) {
      const arr = grouped.get(doc.venture_node_id) ?? []
      arr.push(doc)
      grouped.set(doc.venture_node_id, arr)
    }
    return grouped
  }, [data])

  const selectedDoc = useMemo(
    () => data?.documents.find((d) => d.id === selectedDocId) ?? null,
    [data, selectedDocId]
  )

  const visibleChunks = useMemo(
    () => selectedDoc?.chunks.slice(0, chunkPage * CHUNKS_PER_PAGE) ?? [],
    [selectedDoc, chunkPage]
  )

  // Nodes with RAG sources
  const nodesWithSources = useMemo(
    () =>
      nodes.filter(
        (n) =>
          Array.isArray(n.data?.ragSources) &&
          (n.data.ragSources as { fileName: string; excerpt: string }[]).length > 0
      ),
    [nodes]
  )

  const toggleSourceNode = useCallback((nodeId: string) => {
    setExpandedSourceNodes((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }, [])

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // ─── Tab: Dashboard ──────────────────────────────────────────────────────

  const renderDashboard = () => {
    if (!data) return null
    const { stats } = data

    return (
      <TabContent>
        <StatsGrid>
          <StatCard $accent="#6e53f9">
            <StatValue>{stats.totalDocuments}</StatValue>
            <StatLabel>Documents</StatLabel>
          </StatCard>
          <StatCard $accent="#3b82f6">
            <StatValue>{stats.totalChunks.toLocaleString()}</StatValue>
            <StatLabel>Chunks</StatLabel>
          </StatCard>
          <StatCard $accent="#8b5cf6">
            <StatValue>{stats.totalTokens.toLocaleString()}</StatValue>
            <StatLabel>Tokens</StatLabel>
          </StatCard>
          <StatCard $accent="#22c55e">
            <StatValue>{stats.byStatus['ready'] ?? 0}</StatValue>
            <StatLabel>Ready</StatLabel>
          </StatCard>
          <StatCard $accent="#eab308">
            <StatValue>{stats.byStatus['processing'] ?? 0}</StatValue>
            <StatLabel>Processing</StatLabel>
          </StatCard>
          <StatCard $accent="#ef4444">
            <StatValue>{stats.byStatus['error'] ?? 0}</StatValue>
            <StatLabel>Errors</StatLabel>
          </StatCard>
        </StatsGrid>

        <SectionTitle>By Venture Node</SectionTitle>
        <VentureTable>
          <VentureTableHeader>
            <div>Venture Node</div>
            <div>Docs</div>
            <div>Chunks</div>
            <div>Tokens</div>
          </VentureTableHeader>
          {Object.entries(stats.byVentureNode).map(([nodeId, s]) => {
            const nodeInfo = nodeLabelMap.get(nodeId)
            return (
              <VentureTableRow key={nodeId}>
                <div>
                  <div style={{ fontWeight: FONT_WEIGHT.medium }}>
                    {nodeInfo?.label ?? nodeId.slice(0, 12)}
                  </div>
                  <div
                    style={{
                      fontSize: TYPOGRAPHY.caption.fontSize,
                      color: 'var(--color-text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {nodeId.slice(0, 20)}...
                  </div>
                </div>
                <div>{s.docs}</div>
                <div>{s.chunks.toLocaleString()}</div>
                <div>{s.tokens.toLocaleString()}</div>
              </VentureTableRow>
            )
          })}
        </VentureTable>
      </TabContent>
    )
  }

  // ─── Tab: Documents & Chunks ─────────────────────────────────────────────

  const renderDocuments = () => {
    if (!data) return null

    return (
      <SplitPane>
        <DocListPane>
          {data.documents.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <FileText size={32} />
              </EmptyStateIcon>
              <EmptyStateText>No documents uploaded yet</EmptyStateText>
            </EmptyState>
          ) : (
            Array.from(docsByVenture.entries()).map(([ventureId, docs]) => {
              const nodeInfo = nodeLabelMap.get(ventureId)
              return (
                <div key={ventureId}>
                  <DocGroupHeader>{nodeInfo?.label ?? ventureId.slice(0, 16)}</DocGroupHeader>
                  {docs.map((doc) => (
                    <DocRow
                      key={doc.id}
                      $active={doc.id === selectedDocId}
                      onClick={() => {
                        setSelectedDocId(doc.id)
                        setChunkPage(1)
                      }}
                    >
                      <DocFileName>{doc.file_name}</DocFileName>
                      <DocMeta>
                        <StatusBadge $status={doc.status}>{doc.status}</StatusBadge>
                        <span>{doc.chunks.length} chunks</span>
                        <span>{formatBytes(doc.file_size)}</span>
                      </DocMeta>
                      {doc.summary && <DocSummary>{doc.summary}</DocSummary>}
                    </DocRow>
                  ))}
                </div>
              )
            })
          )}
        </DocListPane>

        <ChunkPane>
          {!selectedDoc ? (
            <EmptyState>
              <EmptyStateIcon>
                <Database size={32} />
              </EmptyStateIcon>
              <EmptyStateText>Select a document to inspect its chunks</EmptyStateText>
            </EmptyState>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <SectionTitle style={{ marginBottom: 4 }}>{selectedDoc.file_name}</SectionTitle>
                <div
                  style={{
                    fontSize: TYPOGRAPHY.caption.fontSize,
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {selectedDoc.chunks.length} chunks &middot; {formatDate(selectedDoc.created_at)}
                  {selectedDoc.error_message && (
                    <span style={{ color: '#dc2626', marginLeft: 8 }}>
                      Error: {selectedDoc.error_message}
                    </span>
                  )}
                </div>
                {selectedDoc.summary && (
                  <div
                    style={{
                      fontSize: TYPOGRAPHY.caption.fontSize,
                      color: 'var(--color-text-secondary)',
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {selectedDoc.summary}
                  </div>
                )}
              </div>

              {visibleChunks.map((chunk) => (
                <ChunkCard key={chunk.id}>
                  <ChunkHeader>
                    <span>Chunk #{chunk.chunk_index}</span>
                    <TokenBadge>{chunk.token_count} tokens</TokenBadge>
                  </ChunkHeader>
                  <ChunkContent>{chunk.content}</ChunkContent>
                </ChunkCard>
              ))}

              {selectedDoc.chunks.length > chunkPage * CHUNKS_PER_PAGE && (
                <LoadMoreButton onClick={() => setChunkPage((p) => p + 1)}>
                  Show more ({selectedDoc.chunks.length - chunkPage * CHUNKS_PER_PAGE} remaining)
                </LoadMoreButton>
              )}
            </>
          )}
        </ChunkPane>
      </SplitPane>
    )
  }

  // ─── Tab: Node Sources ───────────────────────────────────────────────────

  const renderNodeSources = () => {
    if (nodesWithSources.length === 0) {
      return (
        <TabContent>
          <EmptyState>
            <EmptyStateIcon>
              <FileText size={32} />
            </EmptyStateIcon>
            <EmptyStateText>No nodes with RAG sources found in this mindmap</EmptyStateText>
          </EmptyState>
        </TabContent>
      )
    }

    return (
      <TabContent>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 80px',
            padding: '8px 16px',
            fontSize: TYPOGRAPHY.caption.fontSize,
            fontWeight: FONT_WEIGHT.semibold,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 8,
          }}
        >
          <div>Node</div>
          <div>Category</div>
          <div>Sources</div>
          <div></div>
        </div>
        {nodesWithSources.map((node) => {
          const sources = node.data?.ragSources as { fileName: string; excerpt: string }[]
          const isExpanded = expandedSourceNodes.has(node.id)
          const nodeInfo = nodeLabelMap.get(node.id)

          return (
            <SourceNodeRow key={node.id}>
              <SourceNodeHeader $expanded={isExpanded} onClick={() => toggleSourceNode(node.id)}>
                <div
                  style={{
                    fontWeight: FONT_WEIGHT.medium,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {nodeInfo?.label ?? node.id.slice(0, 20)}
                </div>
                <CategoryBadge>{nodeInfo?.category ?? 'unknown'}</CategoryBadge>
                <div>
                  {sources.length} source{sources.length !== 1 ? 's' : ''}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
              </SourceNodeHeader>
              {isExpanded && (
                <SourceExcerpts>
                  {sources.map((src, i) => (
                    <ExcerptBlock key={i}>
                      <ExcerptSource>{src.fileName}</ExcerptSource>
                      {src.excerpt}
                    </ExcerptBlock>
                  ))}
                </SourceExcerpts>
              )}
            </SourceNodeRow>
          )
        })}
      </TabContent>
    )
  }

  // ─── Main Render ─────────────────────────────────────────────────────────

  const tabItems = [
    {
      key: 'dashboard',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChart3 size={14} /> Dashboard
        </span>
      ),
      children: renderDashboard(),
    },
    {
      key: 'documents',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={14} /> Documents & Chunks
        </span>
      ),
      children: renderDocuments(),
    },
    {
      key: 'sources',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Database size={14} /> Node Sources
        </span>
      ),
      children: renderNodeSources(),
    },
  ]

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Database size={18} />
          RAG Inspector
        </span>
      }
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ maxWidth: 1200, top: 20 }}
      destroyOnHidden
    >
      <InspectorBody>
        {loading ? (
          <LoadingPulse>Loading RAG data...</LoadingPulse>
        ) : (
          <Tabs items={tabItems} defaultActiveKey="dashboard" style={{ flex: 1 }} />
        )}
      </InspectorBody>
    </Modal>
  )
}
